import {
  EtatCni,
  EtatDer,
  EtatGlobalInvestisseur,
  EtatQpi,
  EtatRecueil,
  InvestisseurFilterInput,
  PatrimoineCategorieReference,
} from '@lib/models/generated/graphql';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { LineOperator } from './filtre-builders/filtre-builder.model';

export const recueilOptions = [
  { value: [EtatRecueil.ACompleter], label: 'À compléter', disabled: false },
  { value: [EtatRecueil.ASigner], label: 'À signer', disabled: false },
  {
    value: [EtatRecueil.EnSignatureApresExpiration, EtatRecueil.EnSignatureApresSigne, EtatRecueil.EnSignaturePremierFois],
    label: 'En signature',
    disabled: false,
  },
  { value: [EtatRecueil.Signe], label: 'Signé', disabled: false },
  { value: [EtatRecueil.Expire], label: 'Expiré', disabled: false },
];
export const qpiStatutOptions = [
  { value: [EtatQpi.ACompleter], label: 'À compléter', disabled: false },
  { value: [EtatQpi.ASigner], label: 'À signer', disabled: false },
  {
    value: [EtatQpi.EnSignatureApresExpiration, EtatQpi.EnSignatureApresSigne, EtatQpi.EnSignaturePremierFois],
    label: 'En signature',
    disabled: false,
  },
  { value: [EtatQpi.Signe, EtatQpi.SigneNouveauACompleter, EtatQpi.SigneNouveauASigner], label: 'Signé', disabled: false },
  { value: [EtatQpi.Expire], label: 'Expiré', disabled: false },
];
export const derStatutOptions: OuiSelectOption[] = [
  { value: EtatDer.ACompleter, label: 'À compléter', disabled: false },
  { value: EtatDer.ASigner, label: 'À signer', disabled: false },
  { value: [EtatDer.EnSignature, EtatDer.EnSignatureApresSigne], label: 'En signature', disabled: false },
  { value: EtatDer.Signe, label: 'Signé', disabled: false },
];

export const pieceIdentiteStatutOptions: OuiSelectOption[] = [
  { value: EtatCni.Manquant, label: 'Manquante', disabled: false },
  { value: EtatCni.Ajoute, label: 'Ajoutée', disabled: false },
  { value: EtatCni.Expire, label: 'Expirée', disabled: false },
];
export const statutInvestisseurOptions: OuiSelectOption[] = [
  { value: 'CONTACT', label: 'Contact', disabled: false },
  { value: 'PROSPECT', label: 'Prospect', disabled: false },
  { value: 'CLIENT', label: 'Client', disabled: false },
];

export const myStelliumStatusOptions: OuiSelectOption[] = [
  { value: ['MAGIC_LINK'], label: 'À inviter', disabled: false },
  { value: ['ENTRA_INVITED'], label: 'Invité', disabled: false },
  { value: ['ENTRA_CONNECTED'], label: 'Activé', disabled: false },
  { value: ['ENTRA_DISABLED'], label: 'Supprimé', disabled: false },
];

export const relationSecondaireOptions: OuiSelectOption[] = [
  { value: 'SECONDAIRE', label: 'Oui', disabled: false },
  { value: 'PRIMAIRE', label: 'Non', disabled: false },
];

export const connaissanceClientAvancementOptions: OuiSelectOption[] = [
  { value: EtatGlobalInvestisseur.AActualiser, label: 'À actualiser', disabled: false },
];
export const fullComparator: LineOperator[] = ['eq', 'gt', 'gte', 'lt', 'lte'];
export const inOperator: LineOperator[] = ['in'];
export const inNotInOperator: LineOperator[] = ['in', 'nin'];
export const eqOperator: LineOperator[] = ['eq'];

export const booleanOptions: OuiSelectOption[] = [
  { label: 'Oui', value: true },
  { label: 'Non', value: false },
];

export const personnePhysiqueOnlyFilter: InvestisseurFilterInput = { personnePhysique: { any: true } };

export const qpiProfilOptions: OuiSelectOption<number>[] = [
  { value: 1, label: 'Sécuritaire' },
  { value: 2, label: 'Défensif' },
  { value: 3, label: 'Equilibré' },
  { value: 4, label: 'Dynamique' },
  { value: 5, label: 'Offensif' },
];

export const qpiEsgOptions: OuiSelectOption<number>[] = [
  { value: 0, label: 'Pas de préférence' },
  { value: 1, label: 'Modérée' },
  { value: 2, label: 'Significative' },
  { value: 3, label: 'Forte' },
];

export const qpiConnaissanceOptions: OuiSelectOption<number>[] = [
  { value: 1, label: 'Novice' },
  { value: 2, label: 'Informé' },
  { value: 3, label: 'Expérimenté' },
];

export const qpiTolerancePerteOptions: OuiSelectOption<number>[] = [
  { value: 1, label: 'Très faible' },
  { value: 2, label: 'Faible' },
  { value: 3, label: 'Moyenne' },
  { value: 4, label: 'Élevée' },
  { value: 5, label: 'Très élevée' },
];

export function buildTypeOptions(
  types: { categorie: string; label: string; value: string }[],
  categoriesReferences: PatrimoineCategorieReference[]
): OuiSelectOption<string>[] {
  const categories = [...new Set(types.map(i => i.categorie))];

  // Comparaison : ordre alphabétique, mais "Autre" à la fin
  const compareAlphabeticalWithAutreLast = (a: string, b: string) => {
    const aStartsWithAutre = a.toLowerCase().startsWith('autre');
    const bStartsWithAutre = b.toLowerCase().startsWith('autre');

    if (aStartsWithAutre && !bStartsWithAutre) return 1;
    if (!aStartsWithAutre && bStartsWithAutre) return -1;

    return a.localeCompare(b, 'fr', { sensitivity: 'base' });
  };

  return categories.map(c => {
    const cat0 = categoriesReferences.find(cr => cr.patrimoineCategory === c);
    const cat2 = categoriesReferences.find(cr => cr.subCategory2 === c);
    const cat1 = categoriesReferences.find(cr => cr.subCategory === c);

    const categoryLabel = cat2?.subCategory2Libelle ?? cat1?.subCategoryLibelle ?? cat0?.categoryLibelle ?? 'UNDEFINED';

    const sortedChildren = types
      .filter(t => t.categorie === c)
      .map(t => ({ label: t.label, value: t.value }))
      .sort((a, b) => compareAlphabeticalWithAutreLast(a.label, b.label));

    return {
      label: categoryLabel,
      children: sortedChildren,
    } satisfies OuiSelectOption<string>;
  });
}
